/**
 * Safe localStorage utilities to prevent JSON parsing errors
 * 
 * This module provides safe methods for storing and retrieving data from localStorage
 * to prevent the "[object Object]" is not valid JSON errors.
 */

/**
 * Safely stores data in localStorage with proper JSON stringification
 * @param {string} key - The localStorage key
 * @param {any} value - The value to store (will be stringified if not a string)
 * @returns {boolean} - Success status
 */
export const safeSetItem = (key, value) => {
  try {
    if (typeof key !== 'string') {
      console.warn('localStorage key must be a string:', key);
      return false;
    }

    let stringValue;
    
    // If value is already a string, use it directly
    if (typeof value === 'string') {
      stringValue = value;
    } else if (value === null || value === undefined) {
      // Handle null/undefined values
      stringValue = '';
    } else {
      // Stringify objects, arrays, numbers, booleans, etc.
      stringValue = JSON.stringify(value);
    }

    localStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    console.error('Error setting localStorage item:', error, { key, value });
    return false;
  }
};

/**
 * Safely retrieves and parses data from localStorage
 * @param {string} key - The localStorage key
 * @param {any} defaultValue - Default value if item doesn't exist or parsing fails
 * @param {boolean} parseJSON - Whether to attempt JSON parsing (default: true)
 * @returns {any} - The retrieved and parsed value or default value
 */
export const safeGetItem = (key, defaultValue = null, parseJSON = true) => {
  try {
    if (typeof key !== 'string') {
      console.warn('localStorage key must be a string:', key);
      return defaultValue;
    }

    const item = localStorage.getItem(key);
    
    if (item === null || item === undefined) {
      return defaultValue;
    }

    // If not parsing JSON, return as string
    if (!parseJSON) {
      return item;
    }

    // If item is empty string, return default
    if (item === '') {
      return defaultValue;
    }

    // Try to parse as JSON
    try {
      return JSON.parse(item);
    } catch (parseError) {
      // If parsing fails, return the string value
      console.warn('Failed to parse localStorage item as JSON, returning as string:', parseError, { key, item });
      return item;
    }
  } catch (error) {
    console.error('Error getting localStorage item:', error, { key });
    return defaultValue;
  }
};

/**
 * Safely removes an item from localStorage
 * @param {string} key - The localStorage key
 * @returns {boolean} - Success status
 */
export const safeRemoveItem = (key) => {
  try {
    if (typeof key !== 'string') {
      console.warn('localStorage key must be a string:', key);
      return false;
    }

    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error removing localStorage item:', error, { key });
    return false;
  }
};

/**
 * Safely clears all localStorage items
 * @returns {boolean} - Success status
 */
export const safeClear = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Validates that a string is valid JSON
 * @param {string} str - String to validate
 * @returns {boolean} - Whether the string is valid JSON
 */
export const isValidJSON = (str) => {
  if (typeof str !== 'string') {
    return false;
  }
  
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely stores a JWT token with validation
 * @param {string} token - JWT token to store
 * @param {string} key - Storage key (default: SEC_AUTH_M_TK)
 * @returns {boolean} - Success status
 */
export const safeStoreToken = (token, key = 'sec_auth_m_tk') => {
  try {
    if (!token || typeof token !== 'string') {
      console.warn('Token must be a non-empty string');
      return false;
    }

    // Validate JWT format (3 parts separated by dots)
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      console.warn('Invalid JWT token format');
      return false;
    }

    // Store the token as a string (no JSON stringification needed)
    localStorage.setItem(key, token);
    return true;
  } catch (error) {
    console.error('Error storing token:', error);
    return false;
  }
};

/**
 * Safely retrieves a JWT token
 * @param {string} key - Storage key (default: SEC_AUTH_M_TK)
 * @returns {string|null} - The token string or null
 */
export const safeGetToken = (key = 'sec_auth_m_tk') => {
  try {
    const token = localStorage.getItem(key);
    
    if (!token || typeof token !== 'string') {
      return null;
    }

    // Basic validation - JWT should have 3 parts
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      console.warn('Retrieved token has invalid format');
      return null;
    }

    return token;
  } catch (error) {
    console.error('Error retrieving token:', error);
    return null;
  }
};

export default {
  safeSetItem,
  safeGetItem,
  safeRemoveItem,
  safeClear,
  isValidJSON,
  safeStoreToken,
  safeGetToken
};